'use client';

import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { NavigationLinks, type NavigationLinkItem } from './components/NavigationLinks/NavigationLinks';

type NavigationBarProps = ComponentProps<'div'> & {
  namespace: string;
  links?: NavigationLinkItem[];
};

const SHADOW_BASE_CLASSES = 'pointer-events-none absolute top-0 h-full transition-opacity duration-300';
const LEFT_SHADOW_CLASSES =
  'left-[calc(var(--ll-container-padding)*-1)] xl:left-0 w-[calc(4rem+var(--ll-container-padding))] xl:w-16 bg-gradient-to-r from-background to-transparent';
const RIGHT_SHADOW_CLASSES =
  'right-[calc(var(--ll-container-padding)*-1)] xl:right-0 w-[calc(5rem+var(--ll-container-padding))] xl:w-20 bg-gradient-to-l from-background to-transparent';

function NavigationBar({ namespace, className, links, ...props }: NavigationBarProps) {
  const ulRef = useRef<HTMLUListElement>(null);
  const pathname = usePathname();

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(true);

  useEffect(() => {
    const ul = ulRef.current;
    if (!ul) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = ul;
      setShowLeftShadow(scrollLeft > 0);
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth);
    };

    ul.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => ul.removeEventListener('scroll', handleScroll);
  }, []);

  const routesWithNavigationBar: string[] = [ROUTES.HOME, ROUTES.LOGIN, ROUTES.SIGN_UP, ROUTES.CHECKOUT.THANK_YOU];
  const allowNavigationBar = routesWithNavigationBar.includes(pathname);

  return (
    <div className={twMerge('relative py-3 px-0 xl:py-0 xl:pl-10', allowNavigationBar ? 'block' : 'hidden lg:block', className)} {...props}>
      <div
        data-testid="navigation-bar-left-shadow"
        aria-hidden="true"
        className={twMerge(SHADOW_BASE_CLASSES, LEFT_SHADOW_CLASSES, showLeftShadow ? 'opacity-100 z-10' : 'opacity-0')}
      />
      <NavigationLinks ref={ulRef} namespace={namespace} links={links} />
      <div
        data-testid="navigation-bar-right-shadow"
        aria-hidden="true"
        className={twMerge(SHADOW_BASE_CLASSES, RIGHT_SHADOW_CLASSES, showRightShadow ? 'opacity-100 z-10' : 'opacity-0')}
      />
    </div>
  );
}

export { NavigationBar };
